import { useState } from 'react';
import { BadgeTestButton } from './BadgeTestButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle, CheckCircle, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { validateUserData, resetUserStatsToMatchActualData, DataValidationResult } from '@/utils/dataValidation';
import { syncUserData } from '@/utils/dataSyncUtils';
import { toast } from 'sonner';

export function DataValidationPanel() {
  const { user } = useAuth();
  const [validation, setValidation] = useState<DataValidationResult | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  const handleValidate = async () => {
    if (!user?.id) return;
    
    setIsValidating(true);
    try {
      const result = await validateUserData(user.id);
      setValidation(result);
      
      if (result.isValid) {
        toast.success("Data validation passed - no issues found!");
      } else {
        toast.error(`Found ${result.issues.length} data inconsistency issues`);
      }
    } catch (error) {
      toast.error("Failed to validate data");
      console.error(error);
    } finally {
      setIsValidating(false);
    }
  };

  const handleReset = async () => {
    if (!user?.id || !validation) return;
    
    const confirmed = window.confirm(
      "⚠️ RESET USER STATS\n\nThis will reset all your stats to match actual data:\n\n• Total points will be set to 0\n• Activities logged today will be set to 0\n• Activity counts will be cleared\n• Experience and tokens will be reset\n\nThis action cannot be undone. Continue?"
    );
    
    if (!confirmed) return;
    
    setIsResetting(true);
    try {
      await resetUserStatsToMatchActualData(user.id);
      toast.success("User stats reset successfully!");
      
      // Re-validate after reset
      setTimeout(() => {
        handleValidate();
      }, 1000);
      
    } catch (error) {
      toast.error("Failed to reset user stats");
      console.error(error);
    } finally {
      setIsResetting(false);
    }
  };

  const handleSync = async () => {
    if (!user?.id) return;

    try {
      setIsSyncing(true);
      const result = await syncUserData(user.id);
      
      if (result.success) {
        toast.success(result.message);
        if (result.fixedIssues.length > 0) {
          console.log('✅ Fixed issues:', result.fixedIssues);
        }
      } else {
        toast.error(result.message);
        if (result.errors.length > 0) {
          console.error('❌ Sync errors:', result.errors);
        }
      }
      
      // Re-validate after sync
      setTimeout(() => {
        handleValidate();
      }, 1000);
    } catch (error) {
      console.error('Failed to sync data:', error);
      toast.error('Failed to sync data');
    } finally {
      setIsSyncing(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5" />
          Data Validation & Sync
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="mb-4">
          <BadgeTestButton />
        </div>
        
        <div className="flex gap-2">
          <Button
            onClick={handleValidate}
            disabled={isValidating || !user?.id}
            variant="outline"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isValidating ? 'animate-spin' : ''}`} />
            {isValidating ? 'Validating...' : 'Validate Data'}
          </Button>

          <Button
            onClick={handleSync}
            disabled={isSyncing || !user?.id}
            variant="secondary"
            className="flex items-center gap-2"
          >
            <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
            {isSyncing ? 'Syncing...' : 'Auto Sync'}
          </Button>
          
          {validation && !validation.isValid && (
            <Button
              onClick={handleReset}
              disabled={isResetting}
              variant="destructive"
              className="flex items-center gap-2"
            >
              <RefreshCw className={`w-4 h-4 ${isResetting ? 'animate-spin' : ''}`} />
              {isResetting ? 'Resetting...' : 'Manual Reset'}
            </Button>
          )}
        </div>

        {validation && (
          <div className="space-y-3">
            <Alert variant={validation.isValid ? "default" : "destructive"}>
              <div className="flex items-center gap-2">
                {validation.isValid ? (
                  <CheckCircle className="w-4 h-4 text-green-600" />
                ) : (
                  <AlertTriangle className="w-4 h-4" />
                )}
                <AlertDescription>
                  {validation.isValid 
                    ? "Data validation passed - all data is consistent!" 
                    : `Found ${validation.issues.length} data inconsistency issue(s)`
                  }
                </AlertDescription>
              </div>
            </Alert>

            {!validation.isValid && (
              <div className="space-y-2">
                <h4 className="font-semibold text-sm">Issues Found:</h4>
                <ul className="text-sm space-y-1">
                  {validation.issues.map((issue, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-destructive">•</span>
                      <span>{issue}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <h4 className="font-semibold mb-2">User Stats</h4>
                {validation.userStats ? (
                  <ul className="space-y-1">
                    <li>Total Points: {validation.userStats.total_points}</li>
                    <li>Week Points: {validation.userStats.current_week_points}</li>
                    <li>Activities Today: {validation.userStats.activities_logged_today}</li>
                    <li>Experience: {validation.userStats.experience}</li>
                  </ul>
                ) : (
                  <p className="text-muted-foreground">No stats found</p>
                )}
              </div>
              
              <div>
                <h4 className="font-semibold mb-2">Actual Data</h4>
                <ul className="space-y-1">
                  <li>Activities: {validation.actualCounts.activities}</li>
                  <li>Habits: {validation.actualCounts.habits}</li>
                  <li>Dailies: {validation.actualCounts.dailies}</li>
                  <li>Todos: {validation.actualCounts.todos}</li>
                </ul>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}